import { useState, useEffect } from 'react';
import { getCurrentUser, fetchUserAttributes, signUp, signIn, signOut } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import { touchLastLoginForCurrentUser } from '../lib/touchLastLogin';
import { findBestUserRowByEmail } from '../lib/resolveAppUser';
import {
  attributeProgramReferralSignup,
  currentProductSurface,
} from '../lib/programReferral';

// Generate client after Amplify is configured
const getClient = () => generateClient<Schema>();

export type ProfileType = 'PARENT' | 'CAREGIVER' | 'CLINICIAN' | 'INTERNS';

interface AuthUser {
  id: string;
  email: string;
  profileType: ProfileType | null;
  fName: string;
  lName: string;
  phoneNumber: string;
}

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      if (currentUser) {
        const attributes = await fetchUserAttributes();
        setIsAuthenticated(true);
        setUser({
          id: currentUser.userId,
          email: attributes.email || '',
          profileType: (attributes['custom:profileType'] as ProfileType) || null,
          fName: attributes.given_name || '',
          lName: attributes.family_name || '',
          phoneNumber: attributes.phoneNumber || ''
        });
      }
    } catch (err) {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    email: string,
    password: string,
    fName: string,
    lName: string,
    phoneNumber: string,
    organizationId?: string,
    kidProfileId?: string,
    role?: string
  ) => {
    try {
      // Convert 10-digit US phone number to E.164 format
      let formattedPhone = phoneNumber;
      if (/^\d{10}$/.test(phoneNumber)) {
        formattedPhone = `+1${phoneNumber}`;
      }
      const userAttributes: Record<string, string> = {
        email,
        given_name: fName,
        family_name: lName
      };
      if (formattedPhone && /^\+1\d{10}$/.test(formattedPhone)) {
        userAttributes.phone_number = formattedPhone;
      }
      // Pool expects email as username
      const { userId } = await signUp({
        username: email,
        password,
        options: {
          userAttributes
        }
      });

      // After successful Cognito registration, create user record in our database
      if (userId) {
        try {
          const client = getClient();
         
          // Check for pending invitation to get organizationId
          const pendingInvitationToken = localStorage.getItem('pendingInvitationToken');
          let invitationOrgId: string | undefined | null = organizationId ?? null;
         
          // CRITICAL: Use role parameter FIRST - it's the source of truth
          let invitationRole: string | undefined | null = role ?? null;
         
          console.log('🔐 register - Role parameter received:', role);
          console.log('🔐 register - OrganizationId parameter received:', organizationId);
         
          // Only look up invitation for organizationId if not provided
          if (pendingInvitationToken && (!invitationOrgId || !role)) {
            try {
              const invitations = await client.models.UserInvitation.list({
                filter: {
                  token: { eq: pendingInvitationToken }
                },
                authMode: 'apiKey'
              });
             
              if (invitations.data && invitations.data.length > 0) {
                const invitation = invitations.data.find(inv => inv.status !== 'REGISTERED') || invitations.data[0];
                // Only use invitation data if role/orgId not provided as parameters
                if (!role && invitation.inviteeRole) {
                  invitationRole = invitation.inviteeRole as any;
                  console.log('🔐 register - No role parameter, using invitation role:', invitation.inviteeRole);
                }
                if (!invitationOrgId && invitation.organizationId) {
                  invitationOrgId = invitation.organizationId as any;
                }
                console.log('🔐 register - Final role:', invitationRole, 'Org ID:', invitationOrgId);
              }
            } catch (invError) {
              console.error('❌ Error checking invitation:', invError);
            }
          }
         
          // CRITICAL: Role MUST be set - throw error if not
          if (!invitationRole) {
            console.error('❌ FATAL: No role provided and no invitation found!');
            throw new Error('Role is required for user registration. Please provide a role or invitation.');
          }
         
          console.log('🔐 register - FINAL role being used:', invitationRole);
         
          // Convert null to undefined for userData
          const finalRole = invitationRole ?? undefined;
          const finalOrgId = invitationOrgId ?? undefined;
         
          console.log('🔐 register - Creating user record with role:', finalRole, 'orgId:', finalOrgId);

          const emailLower = email.toLowerCase().trim();
          const existingByEmail = await findBestUserRowByEmail(client, emailLower);
          if (existingByEmail?.id) {
            console.log('🔐 register - User already exists for email, skipping duplicate create:', existingByEmail.id);
            return;
          }
         
          const userData: any = {
            id: userId,
            email: email,
            fName: fName,
            lName: lName,
            phoneNumber: formattedPhone,
            role: finalRole, // CRITICAL: This MUST be the role parameter passed in
            status: finalRole === 'INTERNS' ? 'PENDING' : 'ACTIVE',
            // Set required fields with default values
            username: email.split('@')[0],
            address: 'Not provided',
            dob: new Date().toISOString().split('T')[0],
            mName: '-',
            password: 'email_auth_user' // Required field but not used for auth (handled by Cognito)
          };

          // Add organization association if provided
          if (finalOrgId) {
            userData.organizationId = finalOrgId;
          }

          const createResult = await client.models.User.create(userData, {
            authMode: 'apiKey'
          });

          if (createResult.errors && createResult.errors.length > 0) {
            console.error('❌ User creation errors:', createResult.errors);
            throw new Error(`Failed to create user record: ${JSON.stringify(createResult.errors)}`);
          }

          if (!createResult.data) {
            console.error('❌ User creation returned no data');
            throw new Error('User record creation failed - no data returned');
          }

          // If kid profile ID is provided, associate it with the user.
          // Must use apiKey: Cognito user is not signed in yet (email verify still pending).
          if (kidProfileId) {
            try {
              await client.models.KidProfile.update(
                {
                  id: kidProfileId,
                  parentId: userId,
                  ...(finalOrgId ? { organizationId: finalOrgId } : {}),
                } as never,
                { authMode: 'apiKey' },
              );
            } catch (linkErr) {
              console.warn(
                '⚠️ Kid link deferred until after email verify:',
                linkErr instanceof Error ? linkErr.message : linkErr,
              );
            }
          }

          console.log('✅ User record created successfully!');
          console.log('✅ Expected role:', invitationRole, 'Actual role in DB:', createResult.data.role);
          console.log('✅ Expected orgId:', invitationOrgId, 'Actual orgId in DB:', createResult.data.organizationId);
          console.log('✅ Full user record:', JSON.stringify({
            id: createResult.data.id,
            email: createResult.data.email,
            role: createResult.data.role,
            organizationId: createResult.data.organizationId
          }));

          void attributeProgramReferralSignup({
            referredUserId: createResult.data.id,
            referredRole: createResult.data.role ?? invitationRole ?? null,
            referredEmail: createResult.data.email ?? email,
            referredName: [fName, lName].filter(Boolean).join(' ').trim() || null,
            productSurface: currentProductSurface(),
          });
         
          // VERIFY: Check if role matches what we expected
          if (createResult.data.role !== invitationRole) {
            console.error('❌❌❌ ROLE MISMATCH! Expected:', invitationRole, 'Got:', createResult.data.role);
            throw new Error(`User record created with wrong role! Expected ${invitationRole} but got ${createResult.data.role}`);
          }
        } catch (dbError: any) {
          console.error('❌ Error creating user record in database:', dbError);
          // Re-throw so registration fails if user record can't be created
          throw new Error(`Registration failed: ${dbError.message || 'Could not create user profile'}`);
        }
      }

      return { userId, username: email };
    } catch (error) {
      // Error signing up logged
      throw error;
    }
  };

  const setProfileType = async (profileType: ProfileType) => {
    try {
      // Update the user's profile type in Cognito
      // Note: You'll need to implement the actual API call to update the custom attribute
      // This might require a Lambda function or backend API
     
      // Update local state
      if (user) {
        setUser({ ...user, profileType });
      }
      return true;
    } catch (error) {
      // Error setting profile type logged
      throw error;
    }
  };

  const updateLastLogin = async () => {
    try {
      await touchLastLoginForCurrentUser();
    } catch {
      // non-blocking
    }
  };

  const login = async (email: string, password: string) => {
    try {
      await signIn({ username: email, password });
      await updateLastLogin();
      await checkAuthState();
    } catch (error) {
      // Error signing in logged
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      // Error signing out logged
      throw error;
    }
  };

  return {
    isAuthenticated,
    user,
    isLoading,
    register,
    login,
    logout,
    setProfileType,
    checkAuthState
  };
};


